<script setup lang="ts">
import TypeaheadPicker from '@/components/TypeaheadPicker.vue'
import RemovableList from './RemovableList.vue'
import { getGoalCharacteristics, type GoalCharacteristic } from './access_points_api'
import { usePickedUuids } from './use_picked_uuids'
import { usePickerOptions } from './use_picker_options'

const props = defineProps<{
  id: string
  selected: string[]
  characteristicsMap: Record<string, GoalCharacteristic>
}>()

const emit = defineEmits<{
  'update:selected': [uuids: string[]]
  add: [uuid: string]
  'load-error': [title: string, error: unknown]
}>()

const { options, isLoading } = usePickerOptions(getGoalCharacteristics, error =>
  emit('load-error', 'Error fetching characteristics for autocompletion', error)
)

const { add, remove } = usePickedUuids(
  () => props.selected,
  uuids => emit('update:selected', uuids),
  uuid => emit('add', uuid),
  'characteristic'
)

const labelFor = (uuid: string) => props.characteristicsMap[uuid]?.Label || uuid
</script>

<template>
  <div class="mb-4">
    <label :for="id" class="block mb-1 font-bold">Add characteristics</label>
    <TypeaheadPicker
      :id="id"
      :items="options"
      :item-label="characteristic => characteristic.Label"
      :item-key="characteristic => characteristic.Uuid"
      :disabled="isLoading"
      placeholder="Characteristic name"
      @select="characteristic => add(characteristic.Uuid, characteristic.Label)"
    />
    <RemovableList
      :items="selected"
      :item-key="uuid => uuid"
      :item-label="labelFor"
      noun="characteristic"
      :empty-focus-id="id"
      @remove="remove"
    />
  </div>
</template>
